/**
 * 홈 설정(homeConfig)의 화면 표시 규칙 단일 소스. 관리자 미리보기(HomeHeroPreview)와
 * 학생 앱(ClassicHomeView/TodayHomeView)이 이 파일 하나만 보고 같게 그린다 — 양쪽이
 * 별도로 해석해서 미리보기와 실제 앱이 달라지는 일을 막는다. 전부 순수 함수고
 * 누락 필드는 EMPTY_HOME_CONFIG_DATA 기본값(전부 노출)과 동일하게 동작하므로,
 * 구버전 문서(필드가 없는 config)도 안전하게 읽는다.
 */

/**
 * 점검 배너 표시 조건의 유일한 정의. hero.mode를 따로 요구하지 않는다 — 운영자가
 * "점검 메시지 사용"을 켜면 히어로가 자동으로 점검 모드로 바뀌는 구조(option A)이므로
 * enabled + 비어있지 않은 문구만 확인한다. (배포 시 HomeConfigPage가 hero.mode를
 * 함께 동기화해 저장하지만, 표시 여부 판정에는 사용하지 않는다.)
 */
export function shouldShowHomeMaintenanceBanner(data) {
  return Boolean(
    data?.maintenanceMessage?.enabled === true
    && typeof data?.maintenanceMessage?.message === 'string'
    && data.maintenanceMessage.message.trim().length > 0
  )
}

const FEATURE_GROUPS = ['todayQuickChips', 'classicUtilityRow']

// feature id와 이름이 다른 설정이 그 id를 함께 제어하는 케이스. classicMealMapRow는
// 클래식 홈의 카드 행 게이트로 뷰에서 직접 소비한다(오늘형 칩에는 영향 없음).
const DIRECT_CONTROLLER_KEYS = {
  sugiInstant: ['sugiInstant', 'classicSugiInstantCard'],
}

function isAnyControllerHidden(data, featureId) {
  for (const group of FEATURE_GROUPS) {
    const entry = data?.cards?.[group]?.find(item => item?.id === featureId)
    if (entry && entry.visible === false) return true
  }
  for (const key of DIRECT_CONTROLLER_KEYS[featureId] ?? [featureId]) {
    const direct = data?.cards?.[key]
    if (direct && typeof direct === 'object' && !Array.isArray(direct) && direct.visible === false) return true
  }
  return false
}

/**
 * 홈 내 진입점 숨김 규칙의 유일한 정의. 하나의 feature id에 대해 어떤 설정이든
 * 숨김으로 말하면 숨긴다: disabledFeatures 포함 OR 그 id를 제어하는 카드/칩 항목의
 * visible === false (예: sugiInstant는 todayQuickChips 항목과 classicSugiInstantCard
 * 행 설정 둘 다 제어할 수 있다). 없는 설정은 숨김으로 해석하지 않는다(기본 노출).
 *
 * 스코프: 홈 화면 안의 진입점(칩·카드·유틸 버튼)만. route 자체의 전역 차단이 아니다.
 */
export function isHomeFeatureHidden(data, featureId) {
  if (data?.disabledFeatures?.includes(featureId)) return true
  return isAnyControllerHidden(data, featureId)
}

/**
 * 그룹(todayQuickChips/classicUtilityRow)을 order 오름차순으로 정렬해 돌려준다.
 * visible 필터는 호출부가 isHomeFeatureHidden과 함께 결정한다(숨김 사유가
 * disabledFeatures인지 visible인지 구분할 필요가 있을 때까지).
 * config가 없거나 그룹이 배열이 아니면 빈 배열 — 기본 UI 유지는 호출부 몫이다.
 */
export function getOrderedCardEntries(data, group) {
  const list = data?.cards?.[group]
  if (!Array.isArray(list)) return []
  return [...list].sort((a, b) => (a?.order ?? 0) - (b?.order ?? 0))
}
